import React, { useState, useEffect } from 'react';
import { listingTableSx } from '../components/listingTableSx';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  VerifiedUser as VerifiedUserIcon,
  VpnKey as VpnKeyIcon,
  AdminPanelSettings as IGAIcon,
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  BusinessCenter as BusinessCenterIcon,
} from '@mui/icons-material';
import { authAPI, userProfileAPI, igaAPI, usersAPI } from '../services/api';
import auditService, { AuditEvent } from '../services/auditService';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { IGAEntitlement } from '../types';
import { chipSxFromSurface, getRoleChipStyle, getStatusSurface } from '../utils/appearanceTokens';
import CenteredLoader from '../components/CenteredLoader';

// ─── Style tokens (match newer module pages like Users) ────────────────────────
const TH_SX = {
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
  color: 'text.secondary',
  bgcolor: 'grey.50',
  borderBottom: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap' as const,
  py: 1,
};

const CARD_SX = { borderRadius: 2 };

const TABS_SX = {
  px: 1,
  minHeight: 48,
  '& .MuiTabs-indicator': { height: 3, borderRadius: 3 },
  '& .MuiTabs-flexContainer': { width: '100%' },
  '& .MuiTabs-scroller': { overflow: 'visible !important' },
} as const;

const TAB_SX = {
  minHeight: 48,
  minWidth: 0,
  flex: 1,
  maxWidth: 'none',
  px: { xs: 0.5, sm: 1 },
  py: 0.75,
  fontSize: { xs: '0.68rem', sm: '0.75rem', md: '0.78rem' },
  fontWeight: 700,
  textTransform: 'none' as const,
  flexDirection: 'row',
  gap: 0.5,
  whiteSpace: 'nowrap',
  '& .MuiTab-iconWrapper': { marginBottom: 0, marginRight: 0 },
  '& .MuiSvgIcon-root': { fontSize: { xs: 16, sm: 18 } },
} as const;

interface UserProfile {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  enterprise_id: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  sign_in_source?: string;
}

interface ApplicationOwnership {
  application_id: string;
  application_name: string;
  business_function: string;
  ownership_type: 'primary' | 'secondary';
  assigned_at: string;
  instances: Array<{
    id: string;
    name: string;
    environment: string;
    sso_status: string;
    iga_status: string;
    pam_status: string;
  }>;
}

interface ApplicationDelegation {
  application_id: string;
  application_name: string;
  business_function: string;
  delegation_type: 'application' | 'instance';
  instance_id?: string;
  instance_name?: string;
  delegated_at: string;
  delegated_by: string;
  business_justification?: string;
  status: string;
  instances?: Array<{
    id: string;
    name: string;
    environment: string;
    sso_status: string;
    iga_status: string;
    pam_status: string;
  }>;
}

/** IGA entitlement assigned to the profile user (mapped from API for this page). */
interface AssignedEntitlementRow {
  id: string;
  name: string;
  description: string;
  application_name: string;
  instance_name: string;
  entitlement_type: string;
  environment: string;
  risk_level: string;
  data_classification: string;
  owner_approval_required: boolean;
  security_approval_required: boolean;
  status: string;
  created_at: string;
  last_reviewed: string;
  next_review: string;
  members_count: number;
  approvers: Array<{
    id: string;
    name: string;
    email: string;
    approval_level: number;
  }>;
}

interface PAMAccount {
  id: string;
  name: string;
  account_type: string;
  classification: string;
  application_name: string;
  server_name?: string;
  database_name?: string;
  owner_name: string;
  owner_email: string;
  risk_score: number;
  status: string;
  last_used: string;
  password_expiry: string;
  rotation_policy: string;
  access_frequency: string;
  created_at: string;
  metadata: {
    business_context?: string;
    usage_pattern?: string;
    compliance_notes?: string;
  };
}

interface UserProfileData {
  user: UserProfile;
  primary_owned_applications: ApplicationOwnership[];
  secondary_owned_applications: ApplicationOwnership[];
  delegated_applications: ApplicationDelegation[];
  audit_logs: Array<{
    id: string;
    event_type: string;
    entity_type: string;
    actor: string;
    target: string;
    action_description?: string;
    details: any;
    created_at: string;
  }>;
  iga_entitlements?: AssignedEntitlementRow[];
  pam_accounts?: PAMAccount[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  hiddenSx?: React.CSSProperties;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, hiddenSx, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-profile-tabpanel-${index}`}
      aria-labelledby={`user-profile-tab-${index}`}
      style={hiddenSx}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function mapApiToAssignedEntitlementRow(e: IGAEntitlement): AssignedEntitlementRow {
  const m = e.memberships?.[0];
  const approvers = [
    ...e.primary_approvers.map((a) => ({
      id: a.id,
      name: [a.first_name, a.last_name].filter(Boolean).join(' ').trim() || a.username || a.email,
      email: a.email,
      approval_level: 1
    })),
    ...e.secondary_approvers.map((a) => ({
      id: a.id,
      name: [a.first_name, a.last_name].filter(Boolean).join(' ').trim() || a.username || a.email,
      email: a.email,
      approval_level: 2
    }))
  ];
  const lastReviewed = m?.review_date || m?.access_granted_date || e.updated_at || e.created_at;
  const nextReview = m?.access_expiry_date || m?.review_date || '';
  return {
    id: e.id,
    name: e.name,
    description: e.description,
    application_name: e.application_name,
    instance_name: e.instance_name,
    entitlement_type: e.entitlement_type,
    environment: e.environment || '—',
    risk_level: e.risk_level || 'Medium',
    data_classification: e.data_classification,
    owner_approval_required: !!e.owner_approval_required,
    security_approval_required: !!e.security_approval_required,
    status: (m?.status as string) || e.status,
    created_at: e.created_at,
    last_reviewed: lastReviewed,
    next_review: nextReview,
    members_count: e.member_count ?? 0,
    approvers
  };
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { user: currentUser } = useAuth();
  const { resolvedMode } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProfileData, setUserProfileData] = useState<UserProfileData | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  useEffect(() => {
    if (userId) {
      loadUserProfile();
    }
  }, [userId]);

  useEffect(() => {
    const person = userProfileData?.user;
    if (!person) return;
    const name = `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email || 'Profile';
    document.title = `${name} - PROVYSN.ai`;
  }, [userProfileData]);

  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd) {
      setError('Current password and new password are required');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('New password and confirm password do not match');
      return;
    }
    try {
      setPwdLoading(true);
      setError(null);
      await authAPI.changePassword({ current_password: currentPwd, new_password: newPwd });
      setChangePwdOpen(false);
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (e: any) {
      const api = e?.response?.data;
      setError(api?.error || api?.message || 'Failed to change password');
    } finally {
      setPwdLoading(false);
    }
  };

  const mapAuditEventToProfileLog = (ev: AuditEvent) => {
    let details: Record<string, unknown> = {};
    if (ev.details && typeof ev.details === 'object') {
      details = ev.details as Record<string, unknown>;
    } else if (typeof ev.details === 'string') {
      try {
        details = JSON.parse(ev.details);
      } catch {
        details = {};
      }
    }
    return {
      id: ev.id,
      event_type: ev.event_type,
      entity_type: ev.entity_type,
      actor: ev.user_name || 'Unknown',
      target: ev.entity_name || ev.action_description || ev.entity_type,
      action_description: ev.action_description,
      details,
      created_at: ev.timestamp,
    };
  };

  const loadProfileAuditLogs = async (profileUserId: string) => {
    try {
      const { events } = await auditService.getAuditEvents({ profile_user_id: profileUserId, limit: 50 });
      return events.map(mapAuditEventToProfileLog);
    } catch (e) {
      console.error('Failed to load profile audit logs', e);
      return [];
    }
  };

  // Generate dummy PAM accounts data
  const generateDummyPAMAccounts = (): PAMAccount[] => {
    return [
      {
        id: 'pam-001',
        name: 'postgres-admin',
        account_type: 'database',
        classification: 'Confidential',
        application_name: 'Customer Database',
        server_name: 'db-postgres-01',
        database_name: 'customer_db',
        owner_name: 'David Brown',
        owner_email: 'david.brown@acme.com',
        risk_score: 8,
        status: 'Active',
        last_used: '2024-09-10T08:30:00Z',
        password_expiry: '2024-12-10T00:00:00Z',
        rotation_policy: '90 days',
        access_frequency: 'Daily',
        created_at: '2024-01-15T10:00:00Z',
        metadata: {
          business_context: 'Primary database administrator account for customer data management',
          usage_pattern: 'Used for database maintenance, backup operations, and user management',
          compliance_notes: 'SOX compliant - requires quarterly review'
        }
      },
      {
        id: 'pam-002',
        name: 'salesforce-service-account',
        account_type: 'service',
        classification: 'Internal',
        application_name: 'Salesforce',
        server_name: 'salesforce-prod-01',
        owner_name: 'David Brown',
        owner_email: 'david.brown@acme.com',
        risk_score: 6,
        status: 'Active',
        last_used: '2024-09-11T14:20:00Z',
        password_expiry: '2024-11-11T00:00:00Z',
        rotation_policy: '60 days',
        access_frequency: 'Continuous',
        created_at: '2024-02-01T09:00:00Z',
        metadata: {
          business_context: 'Service account for automated Salesforce integrations and data sync',
          usage_pattern: 'Used by ETL processes and API integrations',
          compliance_notes: 'Regular monitoring required for unusual access patterns'
        }
      },
      {
        id: 'pam-003',
        name: 'root@web-server-01',
        account_type: 'os',
        classification: 'Confidential',
        application_name: 'Web Application',
        server_name: 'web-server-01',
        owner_name: 'David Brown',
        owner_email: 'david.brown@acme.com',
        risk_score: 9,
        status: 'Active',
        last_used: '2024-09-09T16:45:00Z',
        password_expiry: '2024-10-09T00:00:00Z',
        rotation_policy: '30 days',
        access_frequency: 'Weekly',
        created_at: '2024-01-20T11:30:00Z',
        metadata: {
          business_context: 'Root access for web server maintenance and configuration',
          usage_pattern: 'Used for system updates, log analysis, and emergency access',
          compliance_notes: 'High-risk account - requires dual approval for access'
        }
      },
      {
        id: 'pam-004',
        name: 'aws-admin-role',
        account_type: 'cloud_iam',
        classification: 'Restricted',
        application_name: 'Cloud Infrastructure',
        owner_name: 'David Brown',
        owner_email: 'david.brown@acme.com',
        risk_score: 7,
        status: 'Active',
        last_used: '2024-09-08T12:15:00Z',
        password_expiry: '2024-11-08T00:00:00Z',
        rotation_policy: '45 days',
        access_frequency: 'Bi-weekly',
        created_at: '2024-03-05T14:00:00Z',
        metadata: {
          business_context: 'AWS administrative role for cloud resource management',
          usage_pattern: 'Used for infrastructure provisioning and monitoring',
          compliance_notes: 'Cloud security policy compliance required'
        }
      }
    ];
  };

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await userProfileAPI.getUserProfile(userId!);
      const profileData = response.data.data;

      let assignedRows: AssignedEntitlementRow[] = [];
      try {
        const igaRes = await igaAPI.getEntitlementsForUser(userId!);
        const raw = igaRes.data?.data;
        if (igaRes.data?.success && Array.isArray(raw)) {
          assignedRows = raw.map(mapApiToAssignedEntitlementRow);
        }
      } catch {
        // IGA service optional / not deployed: show empty entitlements without failing profile
      }

      profileData.iga_entitlements = assignedRows;
      profileData.pam_accounts = generateDummyPAMAccounts();
      profileData.audit_logs = await loadProfileAuditLogs(userId!);

      setUserProfileData(profileData);
    } catch (error: any) {
      console.error('Error loading user profile:', error);
      setError(error.response?.data?.message || 'Failed to load user profile');
      showError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'approved':
      case 'active':
        return 'success';
      case 'pending':
      case 'in progress':
        return 'warning';
      case 'rejected':
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'sso_admin':
      case 'iga_admin':
      case 'pam_admin':
        return 'primary';
      case 'grc_admin':
        return 'warning';
      case 'auditor':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAccountTypeIcon = (accountType: string) => {
    switch (accountType) {
      case 'database':
        return <StorageIcon />;
      case 'service':
        return <PersonIcon />;
      case 'os':
        return <ComputerIcon />;
      case 'api':
        return <VpnKeyIcon />;
      case 'cloud_iam':
        return <CloudIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const getAccountTypeColor = (accountType: string) => {
    switch (accountType) {
      case 'database':
        return '#2e7d32';
      case 'service':
        return '#9c27b0';
      case 'os':
        return '#ed6c02';
      case 'api':
        return '#1976d2';
      case 'cloud_iam':
        return '#0288d1';
      default:
        return '#666';
    }
  };

  const getRiskScoreColor = (score: number) => {
    if (score >= 8) return '#f44336';
    if (score >= 5) return '#ff9800';
    return '#4caf50';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const f = (firstName || '').trim();
    const l = (lastName || '').trim();
    const fi = f ? f.charAt(0) : '';
    const li = l ? l.charAt(0) : '';
    const initials = `${fi}${li}`.toUpperCase();
    return initials || 'U';
  };

  if (loading) {
    return <CenteredLoader />;
  }

  // Allow super admins to view any profile, or any user to view their own profile
  const isOwnProfile = currentUser?.id === userId;
  if (currentUser?.role !== 'super_admin' && !isOwnProfile) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }>
          Access denied. You can only view your own profile.
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!userProfileData || !userProfileData.user) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          User profile not found
        </Alert>
      </Box>
    );
  }

  const { 
    user, 
    primary_owned_applications = [], 
    secondary_owned_applications = [], 
    delegated_applications = [], 
    audit_logs = [],
    iga_entitlements = [],
    pam_accounts = []
  } = userProfileData;

  const canManageAccount = !!currentUser?.role
    && ['super_admin', 'app_admin', 'iga_admin', 'pam_admin'].includes(currentUser.role)
    && currentUser.id !== user.id;

  const setAccountActive = async (active: boolean) => {
    const label = active ? 'Unlock this account?' : 'Suspend this account?';
    if (!window.confirm(label)) return;
    await usersAPI.updateUser(user.id, { is_active: active });
    setUserProfileData({
      ...userProfileData,
      user: { ...user, is_active: active },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header (consistent with other modules) */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>User Profile</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              View identity details, ownership, audit logs and entitlements
            </Typography>
          </Box>
        </Box>
        {isOwnProfile && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={() => {
              setError(null);
              setChangePwdOpen(true);
            }}
            sx={{ fontSize: '0.78rem', py: 0.5 }}
          >
            Change Password
          </Button>
        )}
      </Box>

      <Dialog open={changePwdOpen} onClose={() => setChangePwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Current password"
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              fullWidth
              autoComplete="current-password"
              InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment> }}
            />
            <TextField
              label="New password"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              fullWidth
              autoComplete="new-password"
              helperText="Minimum 8 characters"
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              fullWidth
              autoComplete="new-password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePwdOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={pwdLoading}>
            {pwdLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Info Card */}
      <Card variant="outlined" sx={{ mb: 2, ...CARD_SX }}>
        <CardContent sx={{ py: '12px !important' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {getInitials(user.first_name, user.last_name)}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.3 }}>
                {user.first_name} {user.last_name}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                <EmailIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {user.email}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>@{user.username}</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={getRoleChipStyle(user.role, resolvedMode).label}
                  size="small"
                  icon={<VerifiedUserIcon />}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    ...chipSxFromSurface(getRoleChipStyle(user.role, resolvedMode)),
                  }}
                />
                <Chip
                  label={user.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    ...chipSxFromSurface(
                      getStatusSurface(user.is_active ? 'success' : 'neutral', resolvedMode)
                    ),
                  }}
                />
              </Box>
            </Grid>
            <Grid item>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                <CalendarIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                Last Login: {user.last_login ? formatDate(user.last_login) : 'Never'}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                Member Since: {formatDate(user.created_at)}
              </Typography>
              {canManageAccount && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="outlined" color="warning" disabled={!user.is_active} onClick={() => void setAccountActive(false)}>
                    Suspend
                  </Button>
                  <Button size="small" variant="outlined" disabled={user.is_active !== false} onClick={() => void setAccountActive(true)}>
                    Unlock
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, ...CARD_SX }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, mb: 1 }}>Sessions</Typography>
          <Typography sx={{ fontSize: '0.82rem' }}>
            Latest sign-in: {user.last_login ? formatDate(user.last_login) : 'Never'}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            {user.sign_in_source || 'Sign-in source is recorded on the directory'}
          </Typography>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={CARD_SX}>
            <CardContent sx={{ py: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessCenterIcon color="primary" sx={{ mr: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Applications Owned</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'primary.main', lineHeight: 1.1 }}>
                {primary_owned_applications.length + secondary_owned_applications.length}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                Primary & secondary ownership
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={CARD_SX}>
            <CardContent sx={{ py: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <IGAIcon color="secondary" sx={{ mr: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>IGA Entitlements</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'secondary.main', lineHeight: 1.1 }}>
                {iga_entitlements.length}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                Access rights & permissions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={CARD_SX}>
            <CardContent sx={{ py: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssignmentIcon color="success" sx={{ mr: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Delegated Apps</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'success.main', lineHeight: 1.1 }}>
                {delegated_applications.length}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                Temporarily delegated access
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card variant="outlined" sx={CARD_SX}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="user profile tabs"
            variant="fullWidth"
            sx={TABS_SX}
          >
            <Tab
              icon={<PersonIcon />}
              iconPosition="start"
              label="Primary Owner"
              id="user-profile-tab-0"
              aria-controls="user-profile-tabpanel-0"
              sx={TAB_SX}
            />
            <Tab
              icon={<BusinessIcon />}
              iconPosition="start"
              label="Secondary Owner"
              id="user-profile-tab-1"
              aria-controls="user-profile-tabpanel-1"
              sx={TAB_SX}
            />
            <Tab
              icon={<AssignmentIcon />}
              iconPosition="start"
              label="Delegated"
              id="user-profile-tab-2"
              aria-controls="user-profile-tabpanel-2"
              sx={TAB_SX}
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label="Audit Logs"
              id="user-profile-tab-3"
              aria-controls="user-profile-tabpanel-3"
              sx={TAB_SX}
            />
            <Tab
              icon={<IGAIcon />}
              iconPosition="start"
              label="IGA Entitlements"
              id="user-profile-tab-4"
              aria-controls="user-profile-tabpanel-4"
              sx={TAB_SX}
            />
          </Tabs>
        </Box>

        {/* Primary Owner Tab */}
        <TabPanel value={activeTab} index={0}>
          <Typography variant="h6" gutterBottom>
            Applications as Primary Owner ({primary_owned_applications.length})
          </Typography>
          {primary_owned_applications.length === 0 ? (
            <Alert severity="info">No applications owned as primary owner.</Alert>
          ) : (
            <List>
              {primary_owned_applications.map((app) => (
                <Accordion key={app.application_id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      {app.application_name} ({app.business_function})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Assigned: {formatDate(app.assigned_at)}
                    </Typography>
                    {app.instances && app.instances.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Instances ({app.instances.length}):
                        </Typography>
                        <Table size="small" sx={listingTableSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Environment</TableCell>
                              <TableCell>SSO Status</TableCell>
                              <TableCell>IGA Status</TableCell>
                              <TableCell>PAM Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(app.instances || []).map((instance) => (
                              <TableRow key={instance.id}>
                                <TableCell>{instance.name}</TableCell>
                                <TableCell>
                                  <Chip label={instance.environment} size="small" color="primary" />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.sso_status} 
                                    size="small" 
                                    color={getStatusColor(instance.sso_status) as any}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.iga_status} 
                                    size="small" 
                                    color={getStatusColor(instance.iga_status) as any}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.pam_status} 
                                    size="small" 
                                    color={getStatusColor(instance.pam_status) as any}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Secondary Owner Tab */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Applications as Secondary Owner ({secondary_owned_applications.length})
          </Typography>
          {secondary_owned_applications.length === 0 ? (
            <Alert severity="info">No applications owned as secondary owner.</Alert>
          ) : (
            <List>
              {secondary_owned_applications.map((app) => (
                <Accordion key={app.application_id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      {app.application_name} ({app.business_function})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Assigned: {formatDate(app.assigned_at)}
                    </Typography>
                    {app.instances && app.instances.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Instances ({app.instances.length}):
                        </Typography>
                        <Table size="small" sx={listingTableSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Environment</TableCell>
                              <TableCell>SSO Status</TableCell>
                              <TableCell>IGA Status</TableCell>
                              <TableCell>PAM Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(app.instances || []).map((instance) => (
                              <TableRow key={instance.id}>
                                <TableCell>{instance.name}</TableCell>
                                <TableCell>
                                  <Chip label={instance.environment} size="small" color="primary" />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.sso_status} 
                                    size="small" 
                                    color={getStatusColor(instance.sso_status) as any}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.iga_status} 
                                    size="small" 
                                    color={getStatusColor(instance.iga_status) as any}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.pam_status} 
                                    size="small" 
                                    color={getStatusColor(instance.pam_status) as any}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Delegated Tab */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Delegated Applications ({delegated_applications.length})
          </Typography>
          {delegated_applications.length === 0 ? (
            <Alert severity="info">No delegated applications.</Alert>
          ) : (
            <List>
              {delegated_applications.map((delegation) => (
                <Accordion key={`${delegation.application_id}-${delegation.instance_id || 'app'}`}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1">
                        {delegation.application_name}
                        {delegation.instance_name && ` - ${delegation.instance_name}`}
                      </Typography>
                      <Chip 
                        label={delegation.delegation_type} 
                        size="small" 
                        color="secondary"
                      />
                      <Chip 
                        label={delegation.status} 
                        size="small" 
                        color={getStatusColor(delegation.status) as any}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Delegated by: {delegation.delegated_by}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Delegated at: {formatDate(delegation.delegated_at)}
                    </Typography>
                    {delegation.business_justification && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Justification: {delegation.business_justification}
                      </Typography>
                    )}
                    {delegation.instances && delegation.instances.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          All Application Instances:
                        </Typography>
                        <Table size="small" sx={listingTableSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Environment</TableCell>
                              <TableCell>SSO Status</TableCell>
                              <TableCell>IGA Status</TableCell>
                              <TableCell>PAM Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(delegation.instances || []).map((instance) => (
                              <TableRow key={instance.id}>
                                <TableCell>{instance.name}</TableCell>
                                <TableCell>
                                  <Chip label={instance.environment} size="small" color="primary" />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.sso_status} 
                                    size="small" 
                                    color={getStatusColor(instance.sso_status) as any}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.iga_status} 
                                    size="small" 
                                    color={getStatusColor(instance.iga_status) as any}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={instance.pam_status} 
                                    size="small" 
                                    color={getStatusColor(instance.pam_status) as any}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Audit Logs Tab */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            Recent Audit Logs ({audit_logs.length})
          </Typography>
          {audit_logs.length === 0 ? (
            <Alert severity="info">No audit activity recorded for this user yet.</Alert>
          ) : (
            <List>
              {audit_logs.map((log) => (
                <Accordion key={log.id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="medium">
                          {log.event_type.replace(/_/g, ' ')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                        <Chip 
                          label={log.entity_type} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={log.actor} 
                          size="small" 
                          color="secondary"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(log.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Event Details
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Event Type:</strong> {log.event_type.replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Entity Type:</strong> {log.entity_type}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Actor:</strong> {log.actor}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Target:</strong> {log.target}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Timestamp:</strong> {formatDate(log.created_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Additional Information
                        </Typography>
                        {log.action_description && (
                          <Typography variant="body2" gutterBottom>
                            <strong>Description:</strong> {log.action_description}
                          </Typography>
                        )}
                        {Object.entries(log.details || {}).map(([key, value]) => (
                          <Typography key={key} variant="body2" gutterBottom>
                            <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </Typography>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </TabPanel>

        {/* IGA Entitlements Tab */}
        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IGAIcon color="primary" />
            IGA Entitlements ({iga_entitlements.length})
          </Typography>
          {iga_entitlements.length === 0 ? (
            <Alert severity="info">No IGA entitlements found.</Alert>
          ) : (
            <List>
              {iga_entitlements.map((entitlement) => (
                <Accordion key={entitlement.id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="medium">
                          {entitlement.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                        <Chip 
                          label={entitlement.entitlement_type} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={entitlement.risk_level} 
                          size="small" 
                          color={getRiskColor(entitlement.risk_level) as any}
                        />
                        <Chip 
                          label={entitlement.status} 
                          size="small" 
                          color={getStatusColor(entitlement.status) as any}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Application Details
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Application:</strong> {entitlement.application_name}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Instance:</strong> {entitlement.instance_name}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Environment:</strong> {entitlement.environment}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Data Classification:</strong> {entitlement.data_classification}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Access Details
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Members:</strong> {entitlement.members_count}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Owner Approval:</strong> 
                          <Chip 
                            label={entitlement.owner_approval_required ? 'Required' : 'Not Required'} 
                            size="small" 
                            color={entitlement.owner_approval_required ? 'warning' : 'success'}
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Security Approval:</strong> 
                          <Chip 
                            label={entitlement.security_approval_required ? 'Required' : 'Not Required'} 
                            size="small" 
                            color={entitlement.security_approval_required ? 'error' : 'success'}
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Description
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {entitlement.description}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Review Schedule
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Last Reviewed:</strong> {formatDate(entitlement.last_reviewed)}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Next Review:</strong> {formatDate(entitlement.next_review)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Approvers
                        </Typography>
                        {entitlement.approvers.map((approver) => (
                          <Box key={approver.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PersonIcon fontSize="small" />
                            <Typography variant="body2">
                              {approver.name} (Level {approver.approval_level})
                            </Typography>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          )}
        </TabPanel>

        {/* PAM Accounts UI removed */}
      </Card>
    </Box>
  );
};

export default UserProfile;
